import React from 'react';
import { useNavigate } from 'react-router';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { Chip } from '@features/scoreSpec/styles';
import { fetchRunValidation, VALIDATION_STATUSES } from '@services/fmdc';
import { HeaderCell, HeaderRow, LinkButton, Mono, Row, Table, TableScroll } from './styles';
import { VALIDATION_TONES } from './statusTokens';
import { fmdcPaths, queueUrl } from './fmdcUrl';
import { formatCount, formatValidationStatus, EM_DASH } from './format';

// The Validation tab: the report's rules (V001–V024), worst first.

// Rule | Severity | Status | Message | Rows | action
const GRID_COLUMNS =
  'minmax(180px, 1.6fr) minmax(80px, 0.7fr) minmax(100px, 0.9fr) minmax(220px, 2.6fr) minmax(60px, 0.5fr) minmax(90px, 0.8fr)';

const STATUS_ORDER = {
  [VALIDATION_STATUSES.FAIL]: 0,
  [VALIDATION_STATUSES.WARNING]: 1,
  [VALIDATION_STATUSES.PASS]: 2,
  [VALIDATION_STATUSES.NOT_CHECKED]: 3,
};

const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };

const statusRank = (rule) => STATUS_ORDER[rule.status] ?? 4;
const severityRank = (rule) => SEVERITY_ORDER[String(rule.severity || '').toLowerCase()] ?? 3;

// Fail → warning → pass → not checked; within a status High → Medium → Low; then by rule id so
// the order is stable across refreshes.
export const sortValidationRules = (rules) =>
  [...rules].sort(
    (a, b) =>
      statusRank(a) - statusRank(b) ||
      severityRank(a) - severityRank(b) ||
      String(a.rule_id || '').localeCompare(String(b.rule_id || ''))
  );

// The queue has no per-rule filter yet (the plan's "cells view filtered by warning code" is a
// follow-up), so a rule's rows open the queue narrowed to the matching validation reason — every
// row the rule flagged is in that set, alongside the other rules' rows of the same status.
export const reasonForRule = (rule) =>
  rule.status === VALIDATION_STATUSES.FAIL ? 'validation_fail' : 'validation_warning';

export const RunValidation = ({ runId }) => {
  const navigate = useNavigate();
  const [rules, setRules] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    const status = { isCanceled: false };
    setError(null);
    const load = async () => {
      const result = await fetchRunValidation(runId);
      if (status.isCanceled) return;
      if (result.error) {
        setError(result.error);
        setRules((prev) => prev || []);
      } else {
        setRules(result.rules);
      }
    };
    load();
    return () => {
      status.isCanceled = true;
    };
  }, [runId, reloadKey]);

  const sorted = React.useMemo(() => (rules ? sortValidationRules(rules) : []), [rules]);

  if (rules === null && !error) {
    return <Body5 isLowContrast>Loading validation report…</Body5>;
  }

  if (error && (!rules || rules.length === 0)) {
    return (
      <div>
        <Body5>{error}</Body5>
        <Container top={12}>
          <SkeletonButton onClick={() => setReloadKey((k) => k + 1)}>Retry</SkeletonButton>
        </Container>
      </div>
    );
  }

  if (sorted.length === 0) {
    return <Body5 isLowContrast>No validation rules were run.</Body5>;
  }

  const openRows = (rule) => {
    const query = queueUrl.serialize({ ...queueUrl.defaults, reason: reasonForRule(rule) }).toString();
    navigate(`${fmdcPaths.review(ROUTES.fmdc, runId)}?${query}`);
  };

  return (
    <TableScroll>
      <Table>
        <HeaderRow $columns={GRID_COLUMNS}>
          <HeaderCell>Rule</HeaderCell>
          <HeaderCell>Severity</HeaderCell>
          <HeaderCell>Status</HeaderCell>
          <HeaderCell>Message</HeaderCell>
          <HeaderCell>Rows</HeaderCell>
          <span />
        </HeaderRow>
        {sorted.map((rule) => (
          <Row key={rule.rule_id} $columns={GRID_COLUMNS} $interactive={false}>
            <div>
              <Mono>{rule.rule_id}</Mono>
              <Container top={4}>
                <Body5 bold>{rule.rule_name || EM_DASH}</Body5>
              </Container>
            </div>
            <Body7>{rule.severity || EM_DASH}</Body7>
            <div>
              <Chip $tone={VALIDATION_TONES[rule.status] || 'neutral'}>{formatValidationStatus(rule.status)}</Chip>
            </div>
            <Body7>{rule.message || EM_DASH}</Body7>
            <Body7>{formatCount(rule.row_count ?? 0)}</Body7>
            <div>
              {Array.isArray(rule.record_ids) && rule.record_ids.length > 0 && (
                <LinkButton onClick={() => openRows(rule)} aria-label={`Show rows flagged by ${rule.rule_id}`}>
                  Show rows
                </LinkButton>
              )}
            </div>
          </Row>
        ))}
      </Table>
    </TableScroll>
  );
};
